<template>
    <div>
        <MapLegendContainer></MapLegendContainer>
        <EventsContainer />
        <GmapMap
            id="map" ref="mapRef"
            :center="getDamCoordinates"
            :zoom="getMapZoom"
            @zoom_changed="handleZoomChange"
            :options="mapOptions"
            @click="handleMapClick"
        >
            <GmapInfoWindow
                id="info-window-id"
                :options="infoWindowOptions"
                :position="infoWindowPosition"
                :opened="getInfoWindowOpened"
                @closeclick="handleThisByClosingActiveWindow"
                @domready="handlePotentialOverlaps"
                :key="forceWindowKey"
            >
                <InfoWindow/>
            </GmapInfoWindow>
            <GmapMarker
                v-for="(station, index) in data.PS_stations"
                :key="'marker' + index"
                :position="getPosition(station)"
                :icon="getIcon(station)"
                :clickable="true"
                :draggable="false"
                @dblclick="handleMarkerDblClicked(station)"
                @click="handleClickOnMarker(station)"
            ></GmapMarker>
        </GmapMap>
    </div>
</template>

<script>
    // external
    import {gmapApi} from 'vue2-google-maps';
    // assets
    import {iconColors, mapColorSettings} from "../../constants/map-constants";
    // vuex
    import {
        SET_CURRENT_DAM,
        SET_INFO_WINDOW_OPEN,
        SET_INFO_WINDOW_CLOSE,
        SET_ACTIVE_STATION,
        SET_MAP_ZOOM,
        SET_SHOW_SENSOR_ICONS
    } from "../../store/actions/dashboard";
    import {mapGetters, mapActions} from "vuex";
    // utils
    import {checkIfElementsCollide} from "../../utils/common";
    // components
    import InfoWindow from './InfoWindow';
    import MapLegendContainer from "./MapLegendContainer";
    import EventsContainer from "./EventsContainer";
    // icons
    import Green from '@/assets/img/pin-green.svg';
    import Red from '@/assets/img/pin-red.svg';
    import Yellow from '@/assets/img/pin-orange.svg';
    import Grey from '@/assets/img/pin-grey.svg';

    /**
     * Wraps the Gmap component and its sub components.
     * This is the real map.
     *
     */

    export default {
        name: "MapDams",
        props: {
            data: Object
        },
        components: {
            MapLegendContainer,
            InfoWindow,
            EventsContainer
        },
        data() {
            return {
                map: null,
                mapOptions: {
                    zoomControl: true,
                    mapTypeControl: false,
                    streetViewControl: false,
                    styles: mapColorSettings
                },
                infoWindowOptions: {
                    pixelOffset: {
                        width: 0,
                        height: -14
                    }
                },
                forceWindowKey: 0,
                infoWindowOpened: true,
                legendSelector: false,   // overlaps checks
                eventsSelector: false
            }
        },
        async mounted() {
            // At this point, the child GmapMap has been mounted, but
            // its map has not been initialized.
            // Therefore we need to write mapRef.$mapPromise.then(() => ...)

            // this.$refs.mapRef.$mapPromise.then((map) => {
            //   map.panTo({lat: 1.38, lng: 103.80})
            // })

            const map = await this.$refs.mapRef.$mapPromise;
            this.map = map;

            if (this.data.PS_stations) {
                const bounds = new this.google.maps.LatLngBounds();

                for (let station of this.data.PS_stations) {
                    bounds.extend(new this.google.maps.LatLng(station[2], station[3]))
                }

                this.map.fitBounds(bounds);
                this.google.maps.event.trigger(map, 'resize');
            }
        },
        computed: {
            ...mapGetters([
                'getDamCoordinates',
                'getMapZoom',
                'getInfoWindowOpened',
                'getActiveStation'
            ]),
            google: gmapApi,
            /**
             * Computed latitude and longitude for the active station.
             */
            infoWindowPosition() {
                return {
                    lat: parseFloat(this.getActiveStation[2]),
                    lng: parseFloat(this.getActiveStation[3])
                };
            },
            /**
             * Dynamic translation for the name of the active station.
             */
            activeStationName() {
                if (this.getActiveStation.length === 0) {
                    return '';
                }

                if (this.$i18n.locale && this.$i18n.locale !== 'en') {
                    return `${this.getActiveStation[6]}`;
                }
                return `${this.getActiveStation[0]} #${this.getActiveStation[1]}`
            }
        },
        methods: {
            ...mapActions([
                SET_INFO_WINDOW_OPEN,
                SET_INFO_WINDOW_CLOSE,
                SET_ACTIVE_STATION,
                SET_MAP_ZOOM,
                SET_SHOW_SENSOR_ICONS
            ]),
            /**
             * Extracts the coordinates for the given station.
             */
            getPosition(station) {
                return {
                    lat: parseFloat(station[2]),
                    lng: parseFloat(station[3])
                };
            },
            handleZoomChange(zoomLevel) {
                this.SET_MAP_ZOOM(zoomLevel);
            },
            /**
             * Generates the properties, needed for each GIcon component.
             * Usually those are circles - green, yellow and red.
             */
            getIcon(station) {
                if (!this.google) {
                    return {};
                }

                const icons = {
                    Red,
                    Green,
                    Yellow,
                    Grey
                };

                return {url: icons[iconColors[station[4]]] || Grey}
            },
            /**
             * Can be used to pan the map to the specified
             * position.
             * NOTE: very useful when search is made
             * as to pan on the found station.
             */
            panTo(position) {
                if (!this.map) return;

                this.map.panTo(position);
            },
            /**
             * Marks a station to be the active one.
             * Shows the single info window (bubble above).
             * USED by left click.
             * Also forces the InfoWindow to refresh as to use the
             * DOM ready event.
             */
            handleClickOnMarker(station) {
                 this.SET_SHOW_SENSOR_ICONS(false);
                 this.SET_ACTIVE_STATION(station);
                 if (this.getInfoWindowOpened) {
                     this.forceWindowKey++;

                 } else {
                     this.SET_INFO_WINDOW_OPEN();
                 }
            },
            /**
             * Hides the info window (bubble).
             * USED by hover, click on map, X btn
             */
            handleThisByClosingActiveWindow() {
                this.SET_ACTIVE_STATION([]);
                this.SET_INFO_WINDOW_CLOSE();
            },
            /**
             * When a station is clicked,
             * open the right panel, set the station as
             * the current opened one in the store.
             */
            handleMarkerDblClicked(station) {
                this.$store.dispatch(SET_CURRENT_DAM, station);
            },
            handleMapClick() {
                if (this.getInfoWindowOpened) {
                    this.handleThisByClosingActiveWindow();
                }
            },
            checkIfWindowOverlaps() {
                const classSelectors = document.getElementsByClassName('gm-style-iw gm-style-iw-c');
                const infoWindowSelector = classSelectors ? classSelectors[0] : false;
                // no info window, no problem
                if (!infoWindowSelector) {
                    return false;
                }

                if (this.legendSelector === false) {
                    this.legendSelector = document.getElementById('map-legend-container-id');
                }
                if (this.eventsSelector === false) {
                    this.eventsSelector = document.getElementById('events-container-id');
                }

                // get coordinates
                const legendCoordinates = this.legendSelector.getBoundingClientRect();
                const eventsCoordinates = this.eventsSelector.getBoundingClientRect();
                const infoWinCoordinates = infoWindowSelector.getBoundingClientRect();

                // check if any overlaps
                return (checkIfElementsCollide(legendCoordinates, infoWinCoordinates) ||
                        checkIfElementsCollide(eventsCoordinates, infoWinCoordinates));
            },
            handlePotentialOverlaps() {
                if (this.checkIfWindowOverlaps()) {
                    this.panTo(this.infoWindowPosition);
                }
            }
        }
    }

    /******************  DEV comments *********************
     * if icons are to be used:
     *
    // import Red from "../assets/img/Red.png";
    // import Yellow from "../assets/img/Yellow.png";
    // import Green from "../assets/img/Green.png";

    if using icons
        // const icons = {
        //     'Red': Red,
        //     'Yellow': Yellow,
        //     'Green': Green
        // };
        //
        // return icons[iconColors[station[4]]] || Red;
    */
   /**
    * To unclock zoom in functionality and hook for the zoom event:
    * <GmapMap
    *  :options="{zoomControl: true}"
    *  @zoom_changed="handleZoomChange"
    *  />
    * ===========================================
    * To catch the close event of Info Window:
    * <GmapInfoWindow />
   */
</script>

<style lang="scss">
    @import "../../assets/scss/variables";

    #map {
        width: 100%;
        height: 100%; /*IE?*/
        height: calc(100vh - 154px);
        // move google logo upwards
        // https://stackoverflow.com/questions/34276266/move-google-maps-logo-above-custom-interface-on-bottom-javascript
        div {
            div {
                a[target='_blank'] {
                    div {
                        margin-bottom: 60px;
                    }
                }
            }
        }
    }

    @include media-breakpoint-down(md) {
        #map {
            width: 100%;
            height: calc(100vh - 160px);
        }
    }
</style>